// SchemaService.js - Service for fetching and caching schema data
import axios from 'axios';

class SchemaService {
  constructor() {
    this.cache = new Map();
    this.lookupCache = new Map();
    this.schemaProcessor = null;
  }

  // Initialize schema service with server data
  async initialize() {
    try {
      const [schemaDefinitions, recordTypes, lookupTypes] = await Promise.all([
        this.fetchSchemaDefinitions(),
        this.fetchRecordTypes(),
        this.fetchLookupTypes()
      ]);

      // Import and initialize SchemaProcessor with server data
      const { default: SchemaProcessor } = await import('components/schema/SchemaProcessor');
      this.schemaProcessor = new SchemaProcessor(schemaDefinitions);

      // Cache record types and lookup types
      this.cache.set('recordTypes', recordTypes);
      this.cache.set('lookupTypes', lookupTypes);
      return {
        schemaProcessor: this.schemaProcessor,
        recordTypes,
        lookupTypes
      };
    } catch (error) {
      console.error('Failed to initialize schema service:', error);
      console.error('Error details:', error.message, error.stack);
      throw error;
    }
  }

  // Fetch all schema definitions from server
  async fetchSchemaDefinitions() {
    try {
      const response = await axios.get('/api/schema/definitions', {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch schema definitions:', error.response?.status, error.response?.data);
      throw error;
    }
  }

  // Fetch available record types
  async fetchRecordTypes() {
    try {
      const response = await axios.get('/api/schema/record-types', {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch record types:', error.response?.status, error.response?.data);
      throw error;
    }
  }

  // Fetch available lookup types
  async fetchLookupTypes() {
    try {
      const response = await axios.get('/api/schema/lookup-types', {
        withCredentials: true
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch lookup types:', error.response?.status, error.response?.data);
      throw error;
    }
  }

  // Get schema for specific record type
  getSchemaForRecord(recordType) {
    if (!this.schemaProcessor) {
      throw new Error('Schema service not initialized');
    }
    return this.schemaProcessor.getSchemaForRecord(recordType);
  }

  // Get all editable fields for a record type
  getEditableFields(recordType) {
    if (!this.schemaProcessor) {
      throw new Error('Schema service not initialized');
    }
    return this.schemaProcessor.getEditableFields(recordType);
  }

  // Get all lookup fields for a record type
  getLookupFields(recordType) {
    if (!this.schemaProcessor) {
      throw new Error('Schema service not initialized');
    }
    return this.schemaProcessor.getLookupFields(recordType);
  }

  // Get display fields for table rendering (sorted by display_order)
  getDisplayFields(recordType, maxFields = 8) {
    if (!this.schemaProcessor) {
      throw new Error('Schema service not initialized');
    }
    
    const schema = this.schemaProcessor.getSchemaForRecord(recordType);
    const fields = Object.entries(schema)
      .filter(([fieldName, fieldDef]) => {
        // Exclude meta fields and include fields with display_order defined
        return fieldName !== '_meta' && 
               fieldName !== 'extends' && 
               fieldName !== 'type' &&
               fieldDef.display_order !== undefined;
      })
      .sort(([, a], [, b]) => (a.display_order || 999) - (b.display_order || 999))
      .slice(0, maxFields);

    return Object.fromEntries(fields);
  }

  // Get required lookup data for a record type
  async getRequiredLookupData(recordType) {
    if (!this.schemaProcessor) {
      throw new Error('Schema service not initialized');
    }

    const lookupFields = this.schemaProcessor.getLookupFields(recordType);
    const requiredLookups = new Set();
    
    Object.values(lookupFields).forEach(fieldDef => {
      if (fieldDef.lookup) {
        requiredLookups.add(fieldDef.lookup);
      }
    });

    const lookupData = {};
    
    for (const lookupType of requiredLookups) {
      try {
        lookupData[lookupType] = await this.fetchLookupData(lookupType);
      } catch (error) {
        console.error(`Failed to load lookup data for ${lookupType}:`, error);
        lookupData[lookupType] = [];
      }
    }

    return lookupData;
  }

  // Fetch lookup data with caching
  async fetchLookupData(lookupType) {
    const cacheKey = `lookup_${lookupType}`;
    
    // Check cache first
    if (this.lookupCache.has(cacheKey)) {
      const cached = this.lookupCache.get(cacheKey);
      // Check if cache is still valid (5 minutes)
      if (Date.now() - cached.timestamp < 5 * 60 * 1000) {
        return cached.data;
      }
    }

    // Fetch from server
    const response = await axios.get(`/api/appdata/records/${lookupType.toLowerCase()}`);
    const data = response.data.data || response.data;
    
    // Cache the result
    this.lookupCache.set(cacheKey, {
      data: Array.isArray(data) ? data : [],
      timestamp: Date.now()
    });

    return Array.isArray(data) ? data : [];
  }

  // Validate field value against schema
  validateFieldValue(recordType, fieldName, value) {
    if (!this.schemaProcessor) {
      return { valid: true };
    }
    return this.schemaProcessor.validateFieldValue(recordType, fieldName, value);
  }

  // Get field display configuration
  getFieldDisplayConfig(recordType, fieldName) {
    const schema = this.getSchemaForRecord(recordType);
    const fieldDef = schema[fieldName];
    
    if (!fieldDef) return null;

    return {
      label: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      type: fieldDef.type,
      editable: fieldDef.editable || false,
      required: fieldDef.required || false,
      weight: fieldDef.weight || 0,
      lookup: fieldDef.lookup,
      fields: fieldDef.fields,
      validation: fieldDef.validation || {}
    };
  }

  // Clear caches
  clearCache() {
    this.cache.clear();
    this.lookupCache.clear();
  }
}

// Create singleton instance
const schemaService = new SchemaService();

export default schemaService;